import { useState, useMemo, useCallback } from "react";
import brazilFlag from "../assets/images/brazil-flag.png";
import ukFlag from "../assets/images/uk-flag.png";

/**
 * Hook para gerenciar traduções do fluxo e arquivos
 */
const useFlowTranslations = (intl) => {
  const [availableTranslations, setAvailableTranslations] = useState([]);
  const [fileAvailableTranslations, setFileAvailableTranslations] = useState(
    {}
  );

  // Função para obter todas as traduções disponíveis baseado no idioma atual
  const getAvailableTranslations = useMemo(() => {
    // Mapeamento de locales para keys da API e flags
    const localeMapping = {
      pt: { key: "pt_br", flag: brazilFlag, label: "Português" },
      "pt-BR": { key: "pt_br", flag: brazilFlag, label: "Português" },
      en: { key: "en_us", flag: ukFlag, label: "English" },
      "en-US": { key: "en_us", flag: ukFlag, label: "English" },
    };

    const currentLocale = intl.locale;
    const alternativeLocale =
      currentLocale === "pt" || currentLocale === "pt-BR" ? "en-US" : "pt-BR";
    const alternativeMapping = localeMapping[alternativeLocale];

    return [
      {
        locale: alternativeLocale,
        key: alternativeMapping.key,
        flag: alternativeMapping.flag,
        label: alternativeMapping.label,
      },
    ];
  }, [intl.locale]);

  // Função para inicializar traduções disponíveis
  const initializeAvailableTranslations = useCallback(
    (existingTranslations = {}) => {
      // Filtrar traduções que ainda não existem
      const available = getAvailableTranslations.filter(
        (translation) => !existingTranslations.hasOwnProperty(translation.key)
      );

      setAvailableTranslations(available);
    },
    [getAvailableTranslations]
  );

  // Função para adicionar nova tradução
  const handleAddTranslation = useCallback((translationToAdd, form) => {
    const currentTranslations = form.getValues("nameTranslations") || {};

    // Adicionar campo vazio para a nova tradução
    const updatedTranslations = {
      ...currentTranslations,
      [translationToAdd.key]: "",
    };

    form.setValue("nameTranslations", updatedTranslations);

    // Como acabamos de adicionar uma tradução, não deve haver mais traduções disponíveis
    setAvailableTranslations([]);
  }, []);

  // Função para remover tradução
  const handleRemoveTranslation = useCallback(
    (translationKey, form) => {
      const currentTranslations = form.getValues("nameTranslations") || {};

      // Remover a chave específica do objeto
      const { [translationKey]: removed, ...updatedTranslations } =
        currentTranslations;

      form.setValue("nameTranslations", updatedTranslations);
      form.clearErrors(`nameTranslations.${translationKey}`);

      // Atualizar traduções disponíveis baseado no novo estado
      // Como removemos uma tradução, agora deve haver traduções disponíveis novamente
      const hasRemainingTranslations =
        Object.keys(updatedTranslations).length > 0;

      if (hasRemainingTranslations) {
        setAvailableTranslations([]);
      } else {
        setAvailableTranslations(getAvailableTranslations);
      }
    },
    [getAvailableTranslations]
  );

  // Função para inicializar traduções disponíveis para arquivos
  const initializeFileAvailableTranslations = useCallback(
    (fileIndex, existingTranslations = {}) => {
      // Filtrar traduções que ainda não existem para este arquivo
      const available = getAvailableTranslations.filter(
        (translation) => !existingTranslations.hasOwnProperty(translation.key)
      );

      setFileAvailableTranslations((prev) => ({
        ...prev,
        [fileIndex]: available,
      }));
    },
    [getAvailableTranslations]
  );

  // Função para adicionar nova tradução para arquivo
  const handleAddFileTranslation = useCallback(
    (fileIndex, translationToAdd, form) => {
      const currentTranslations =
        form.getValues(`documents.${fileIndex}.fileNameTranslations`) || {};

      // Adicionar campo vazio para a nova tradução
      const updatedTranslations = {
        ...currentTranslations,
        [translationToAdd.key]: "",
      };

      form.setValue(
        `documents.${fileIndex}.fileNameTranslations`,
        updatedTranslations
      );

      // Como acabamos de adicionar uma tradução, não deve haver mais traduções disponíveis para este arquivo
      setFileAvailableTranslations((prev) => ({
        ...prev,
        [fileIndex]: [],
      }));
    },
    []
  );

  // Função para remover tradução de arquivo
  const handleRemoveFileTranslation = useCallback(
    (fileIndex, translationKey, form) => {
      const currentTranslations =
        form.getValues(`documents.${fileIndex}.fileNameTranslations`) || {};

      // Remover a chave específica do objeto
      const { [translationKey]: removed, ...updatedTranslations } =
        currentTranslations;

      form.setValue(
        `documents.${fileIndex}.fileNameTranslations`,
        updatedTranslations
      );
      form.clearErrors(
        `documents.${fileIndex}.fileNameTranslations.${translationKey}`
      );

      // Atualizar traduções disponíveis baseado no novo estado
      // Como removemos uma tradução, verificar se ainda restam traduções para este arquivo
      const hasRemainingTranslations =
        Object.keys(updatedTranslations).length > 0;

      setFileAvailableTranslations((prev) => ({
        ...prev,
        [fileIndex]: hasRemainingTranslations ? [] : getAvailableTranslations,
      }));
    },
    [getAvailableTranslations]
  );

  return {
    availableTranslations,
    fileAvailableTranslations,
    setFileAvailableTranslations,
    initializeAvailableTranslations,
    handleAddTranslation,
    handleRemoveTranslation,
    initializeFileAvailableTranslations,
    handleAddFileTranslation,
    handleRemoveFileTranslation,
  };
};

export default useFlowTranslations;
